import React, { useState, useEffect } from 'react';
import { User, Garage as GarageInfo, GarageCar } from '../../types';
import { travelService } from '../../services/travelService';
import { gameService } from '../../services/gameService';
import { useError } from '../../contexts/ErrorContext';

interface GarageProps {
  user: User;
  onUpdate: () => void;
  onUserUpdate: (updates: Partial<User>) => void;
}

const Garage: React.FC<GarageProps> = ({ user, onUpdate, onUserUpdate }) => {
  const [garageInfo, setGarageInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useError();

  useEffect(() => {
    loadGarageInfo();
  }, []);

  const loadGarageInfo = async () => {
    try {
      const data = await travelService.getGarageInfo();
      setGarageInfo(data);
    } catch (error) {
      console.error('Error loading garage info:', error);
    }
  };

  const handleSellCar = async (carIndex: number) => {
    setLoading(true);
    
    try {
      const result = await travelService.sellCar(carIndex);
      
      if (result.success) {
        const actualAmount = result.moneyGained || 0;
        const expectedAmount = calculateSellValue(garageInfo.garageCars[carIndex].carId, garageInfo.garageCars[carIndex].carHealth);
        
        showSuccess(`Car sold for $${actualAmount.toLocaleString()}!`);
        console.log(`💰 Garage Success: Car sold for $${actualAmount.toLocaleString()} (Expected: $${expectedAmount.toLocaleString()})`);
        
        // Refresh user data and garage info
        await gameService.refreshUserStats();
        onUpdate();
        await loadGarageInfo();
        
        // Force a small delay to ensure data is updated
        setTimeout(() => {
          console.log('🔄 Garage: Final refresh to ensure money is updated...');
          onUpdate();
        }, 500);
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error selling car';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEquipCar = async (carIndex: number) => {
    setLoading(true);
    console.log(`🚗 Garage: Equipping car at index ${carIndex}`);
    console.log(`🚗 Garage: Current user state - carId: ${user.carId}, equippedCarInstanceId: ${user.equippedCarInstanceId}`);
    
    try {
      const result = await travelService.equipCar(carIndex);
      
      if (result.success && result.updatedUser) {
        console.log(`✅ Garage: Car equipped successfully. New carId: ${result.updatedUser.carId}, carHealth: ${result.updatedUser.carHealth}, equippedCarInstanceId: ${result.updatedUser.equippedCarInstanceId}`);
        showSuccess(result.message);
        // Update user state immediately for instant UI feedback
        onUserUpdate(result.updatedUser);
        console.log(`🔄 Garage: User state updated with:`, result.updatedUser);
        // Refresh garage info to ensure consistency
        await loadGarageInfo();
        // Also refresh from server to ensure consistency
        onUpdate();
        console.log(`🔄 Garage: State updated and refreshed after equipping car`);
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error equipping car';
      console.error(`❌ Garage: Error equipping car:`, error);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUnequipCar = async () => {
    setLoading(true);
    console.log(`🚗 Garage: Unequipping current car (carId: ${user.carId})`);
    
    try {
      const result = await travelService.unequipCar();
      
      if (result.success && result.updatedUser) {
        console.log(`✅ Garage: Car unequipped successfully. New carId: ${result.updatedUser.carId}, carHealth: ${result.updatedUser.carHealth}`);
        showSuccess(result.message);
        // Update user state immediately for instant UI feedback
        onUserUpdate(result.updatedUser);
        // Refresh garage info to ensure consistency
        await loadGarageInfo();
        // Also refresh from server to ensure consistency
        onUpdate();
        console.log(`🔄 Garage: State updated and refreshed after unequipping car`);
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error unequipping car';
      console.error(`❌ Garage: Error unequipping car:`, error);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeGarage = async (garageId: number) => {
    setLoading(true);
    
    try {
      const result = await travelService.upgradeGarage(garageId);
      
      if (result.success) {
        showSuccess(result.message);
        onUpdate();
        loadGarageInfo();
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error upgrading garage';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getCarName = (carId: number): string => {
    const carTypes = [
      'None', 'Honda Civic', 'Toyota Camry', 'BMW 3 Series', 
      'Mercedes C-Class', 'Porsche 911', 'Ferrari F40', 'Lamborghini Countach'
    ];
    return carTypes[carId] || 'Unknown Car';
  };

  const getCarValue = (carId: number): number => {
    // Updated to match backend CAR_TYPES values exactly
    const carValues = [0, 500, 800, 1500, 2000, 5000, 10000, 15000];
    return carValues[carId] || 0;
  };

  const calculateSellValue = (carId: number, carHealth: number): number => {
    const baseValue = getCarValue(carId);
    return Math.floor(baseValue * (carHealth / 100));
  };

  if (!garageInfo) {
    return (
      <div className="garage-page">
        <div className="loading">Loading garage information...</div>
      </div>
    );
  }

  return (
    <div className="garage-page">
      <h3>Garage</h3>
      
      <div className="garage-overview">
        <div className="current-garage">
          <h3>Current Garage</h3>
          <div className="garage-details">
            <p><strong>Type:</strong> {garageInfo.currentGarage.name}</p>
            <p><strong>Capacity:</strong> {garageInfo.currentGarage.capacity} cars</p>
            <p><strong>Description:</strong> {garageInfo.currentGarage.description}</p>
            <p><strong>Cars Stored:</strong> {garageInfo.garageCars.length} / {garageInfo.currentGarage.capacity}</p>
          </div>
        </div>

        <div className="current-car">
          <h3>Current Car</h3>
          <div className="car-details">
            <p><strong>Car:</strong> {user.carId ? getCarName(user.carId) : 'No car equipped'}</p>
            {user.carId && <p><strong>Health:</strong> {user.carHealth}%</p>}
          </div>
        </div>
      </div>

      <div className="cars-section">
        <h3>Your Cars</h3>
        
        {garageInfo.garageCars.length === 0 ? (
          <div className="no-cars">
            <p>Your garage is empty!</p>
            <p>Steal some cars from the Grand Theft Auto section to get started.</p>
          </div>
        ) : (
          <div className="cars-grid">
            {garageInfo.garageCars.map((car: GarageCar, index: number) => {
              const carName = getCarName(car.carId);
              const carValue = getCarValue(car.carId);
              
              return (
                <div key={index} className="car-card">
                  <h4>{carName}</h4>
                  <div className="car-stats">
                    <p><strong>Health:</strong> {car.carHealth}%</p>
                    <p><strong>Base Value:</strong> ${carValue.toLocaleString()}</p>
                    <p><strong>Sell Value:</strong> ${calculateSellValue(car.carId, car.carHealth).toLocaleString()}</p>
                    <p><strong>Stolen:</strong> {new Date(car.stolenAt).toLocaleDateString()}</p>
                  </div>
                  <div className="car-actions">
                    <button
                      className="btn btn-success"
                      onClick={() => handleSellCar(index)}
                      disabled={loading}
                    >
                      Sell Car
                    </button>
                    <button
                      className={`btn ${user.equippedCarInstanceId === car.carInstanceId ? 'btn-danger' : 'btn-primary'}`}
                      onClick={() => user.equippedCarInstanceId === car.carInstanceId ? handleUnequipCar() : handleEquipCar(index)}
                      disabled={loading}
                    >
                      {user.equippedCarInstanceId === car.carInstanceId ? 'Unequip' : 'Equip'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Garage Upgrades */}
      {garageInfo.availableUpgrades.length > 0 && (
        <div className="upgrades-section">
          <h3>Available Upgrades</h3>
          <div className="upgrades-grid">
            {garageInfo.availableUpgrades.map((garage: GarageInfo) => (
              <div key={garage.id} className="upgrade-card">
                <h4>{garage.name}</h4>
                <p className="description">{garage.description}</p>
                <div className="upgrade-stats">
                  <p><strong>Capacity:</strong> {garage.capacity} cars</p>
                  <p><strong>Cost:</strong> ${garage.price.toLocaleString()}</p>
                </div>
                <button
                  className={`btn ${user.money >= garage.price ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleUpgradeGarage(garage.id)}
                  disabled={loading || user.money < garage.price}
                >
                  {user.money >= garage.price ? 'Upgrade' : 'Not Enough Money'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The message display is now handled by the global error context */}
    </div>
  );
};

export default Garage;
